// 3rd party
import * as zipWith from 'lodash.zipwith';

// Bernie
import { Logger } from 'bernie-logger';

// Data
import { preConfiguredQuicklinks } from 'data/pre-configured-quicklinks';

// Models
import { IProgramRelationship, Link, PartnerizeTracking, Product, Program, Quicklink } from 'models';

// Utils
import { getNormalizedProgramLink } from '../util/links';
import { getPartnerizeTrackingLinks, getAtlasPartnerizeTrackingLinks } from '../util/tracking';

class QuicklinkSource {
  private static getProgramQuicklinks(program: Program) {
    return preConfiguredQuicklinks[program.name] ?? [];
  }

  private static getPartnerizeTracking(product: Product, program: IProgramRelationship): PartnerizeTracking {
    return {
      baseUrl: program.networkTrackingBaseUrl,
      camRef: program.camRef,
      creativeRef: product.creativeRef,
    };
  }

  private static getProgramQuicklinkLinkConverter(program: Program): (quicklink: Quicklink) => Link {
    return function convertQuicklinkLink(quicklink: Quicklink): Link {
      return getNormalizedProgramLink(program, quicklink);
    };
  }

  private static convertProgramQuicklinksLinks(program: Program, quicklinks: Quicklink[]): Link[] {
    return quicklinks.map(QuicklinkSource.getProgramQuicklinkLinkConverter(program));
  }

  private static getPartnerizeTrackingLinksFn(product: Product) {
    return product.isAtlasEnabled ? getAtlasPartnerizeTrackingLinks : getPartnerizeTrackingLinks;
  }

  private static mergeQuicklinkLinkTrackingLink(quicklink: Quicklink, link: Link, trackingLink: Link): Quicklink {
    const url = trackingLink?.url || link.url;
    const code = `<a href="${url}">${quicklink.text}</a>`;

    return {
      ...quicklink,
      link: url,
      code,
    };
  }

  private static mergeQuicklinksLinksTrackingLinks(
    quicklinks: Quicklink[],
    links: Link[],
    trackingLinks: Link[],
  ): Quicklink[] {
    return zipWith(quicklinks, links, trackingLinks, QuicklinkSource.mergeQuicklinkLinkTrackingLink);
  }

  public async getCustomQuicklink(
    logger: Logger,
    url: string,
    product: Product,
    program: IProgramRelationship,
  ): Promise<Quicklink> {
    let [quicklink] = QuicklinkSource.getProgramQuicklinks(program);

    quicklink = {
      ...quicklink,
      label: 'custom',
      title: 'Custom Link',
      url,
    };

    const tracking = QuicklinkSource.getPartnerizeTracking(product, program);
    const link = QuicklinkSource.getProgramQuicklinkLinkConverter(program)(quicklink);
    const links = [link];

    const [trackingLink] =
      (await QuicklinkSource.getPartnerizeTrackingLinksFn(product)({ tracking, links, logger })) ?? [];

    quicklink = QuicklinkSource.mergeQuicklinkLinkTrackingLink(quicklink, link, trackingLink);

    return quicklink;
  }

  public async getConfiguredQuicklinks(
    logger: Logger,
    product: Product,
    program: IProgramRelationship,
  ): Promise<Quicklink[]> {
    let quicklinks = QuicklinkSource.getProgramQuicklinks(program);

    const tracking = QuicklinkSource.getPartnerizeTracking(product, program);
    const links = QuicklinkSource.convertProgramQuicklinksLinks(program, quicklinks);

    const trackingLinks = await QuicklinkSource.getPartnerizeTrackingLinksFn(product)({ tracking, links, logger });

    quicklinks = QuicklinkSource.mergeQuicklinksLinksTrackingLinks(quicklinks, links, trackingLinks);

    return quicklinks;
  }
}

export { QuicklinkSource };
